import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { CareerService } from '../../../services//career.service';
import { MetaService } from '../../../services/meta.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-career-details',
  templateUrl: './career-details.component.html',
  styleUrls: ['./career-details.component.scss']
})

export class CareerDetailsComponent implements OnInit {
  data: any;
  pageUrl: string;
  terms: any[] = [];
  ext: string = '';

  showForm = false;
  applyForm: FormGroup;
  resumeFile: File | null = null;
  successMessage: string = '';
  errorMessage: string = '';
  submitAttempted = false;
  isLoading = false;
  siteKey = environment.recaptcha.siteKey;
  captchaResponse: string | null = null;

  contentKeywords = 'Technology Software, Web Development, API integration, PHP Development, Next.js development, React.js development, Node.js development, CMS Development, MVC Development, Android Application, iPhone Application, UI/UX Designing, UI Designing, 3D Designing, Logo Designing, Cloud computing, Artificial intelligence, Data analytics, Cybersecurity, Web development, Mobile app development, IT consulting, Virtual reality, Augmented reality, Web design Website development, Front-end development, Back-end development, Full-stack development, Responsive design, User interface (UI), User experience (UX), Custom web development, E-commerce development, Laravel development, Yii development, Codeigniter development, Content management systems (CMS), WordPress development, Drupal development, Magento development, Shopify development, Mobile-friendly websites, Cross-browser compatibility, Web application development, API integration, Website maintenance';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private careerService: CareerService,
    private metaService: MetaService,
    private router: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {
    this.applyForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      qualification: ['', Validators.required],
      company: [''],
      experience: ['', Validators.required],
      salary: ['', Validators.required],
      resume: [null, Validators.required],
      job_id: ['0', Validators.required]
    });
  }

  executeCaptcha() {
    this.recaptchaV3Service.execute('applyForm')
      .subscribe((token: string) => {
        this.captchaResponse = token;
        console.log('Captcha token:', token);
      });
  }

  ngOnInit(): void {
    const baseUrl = window.location.origin;
    const slug = this.router.snapshot.paramMap.get('slug');

    if (slug) {
      this.careerService.getDetails(slug).subscribe({
        next: (res) => {
          this.data = res.data;
          this.terms = res.data.terms;
          this.pageUrl = encodeURIComponent(window.location.href);

          // Set job_id dynamically
          this.applyForm.patchValue({
            job_id: this.data.id
          });

          // Get file extension if cover_image exists
          if (this.data.cover_image) {
            const parts = this.data.cover_image.split('.');
            this.ext = parts.length > 1 ? parts.pop()!.toLowerCase() : '';
          }

          // Set/update meta tags after data is loaded
          this.metaService.setMetaData({
            title: this.data.title,
            description: this.data.excerpt,
            keywords: this.contentKeywords,
            siteTitle: this.data.title,
            pageUrl: `${baseUrl}${this.router.snapshot['_routerState'].url}`,
            imageUrl: this.data.cover_image ?? `${baseUrl}/assets/img/xipetech.png`,
            ext: this.ext
          });
        },
        error: (err) => console.error('Failed to load job data:', err)
      });
    } else {
      console.error('No slug found in route');
    }
  }
  onImageError(event: any): void {
    event.target.src = '/assets/img/noimage.png';
  }
  
  scrollToForm(): void {
    const element = document.getElementById("apply-job-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  onSubmit(): void {

    this.submitAttempted = true;
    this.successMessage = '';
    this.errorMessage = '';

    if (this.applyForm.invalid) {
      this.applyForm.markAllAsTouched();
      return;
    }

     
    this.executeCaptcha(); 

    const formData = new FormData();
    formData.append('name', this.applyForm.get('name')?.value);
    formData.append('email', this.applyForm.get('email')?.value);
    formData.append('phone', this.applyForm.get('phone')?.value);
    formData.append('qualification', this.applyForm.get('qualification')?.value);
    formData.append('company', this.applyForm.get('company')?.value);
    formData.append('experience', this.applyForm.get('experience')?.value);
    formData.append('salary', this.applyForm.get('salary')?.value);
    formData.append('job_id', this.data.id);  // link job ID
    formData.append('captcha', this.captchaResponse ?? '');

    if (this.resumeFile) {
      formData.append('resume', this.resumeFile);
    }

    this.isLoading = true;

    this.careerService.applyJob(formData).subscribe({
      next: (res) => {
        this.successMessage = res.message ?? 'Your application has been submitted successfully!';
        this.errorMessage = '';
        this.applyForm.reset();
        this.showForm = false;
        this.isLoading = false;
        this.resumeFile = null;
        this.submitAttempted = false;
      },
      error: (err) => {
        console.error('Application failed:', err);
        this.isLoading = false;
        this.errorMessage = err.message ?? 'Failed to submit application. Please try again.';
        this.successMessage = '';
      }
    });
  }
  onFileChange(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Please upload a PDF or Word document.';
        event.target.value = '';
        this.resumeFile = null;
        this.applyForm.patchValue({ resume: null });
        return;
      }

      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than 2MB.';
        event.target.value = '';
        this.resumeFile = null;
        this.applyForm.patchValue({ resume: null });
        return;
      }

      this.resumeFile = file;
      this.applyForm.patchValue({ resume: file });
      this.applyForm.get('resume')?.markAsTouched();
      this.errorMessage = '';
    }
  }

  onlyNumbers(event: any): void {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  // Helper method to check if field has error
  hasError(fieldName: string): boolean {
    const field = this.applyForm.get(fieldName);
    return !!(field?.invalid && (field?.touched || this.submitAttempted));
  }

  // Get error message for specific field
  getErrorMessage(fieldName: string): string {
    const field = this.applyForm.get(fieldName);
    if (field?.errors?.['required']) {
      return 'This field is required';
    } else if (field?.errors?.['email']) {
      return 'Please enter a valid email address';
    } else if (field?.errors?.['maxlength']) {
      return `Maximum length exceeded`;
    }

    return '';
  }
}